import { reactive } from 'vue';
import type { IMenuGroup, IMenuSection } from "@fondationcss/ui";
import { translate } from "@fondationcss/sdk";
import { STUDENT_SUBMISSIONS_FEATURE } from "@/utils/student-submission-utils";
import { STUDENT_REPORTS_FEATURE } from "@/utils/student-portal-features";
import { DEMONSTRATION_FEATURE } from "@/utils/demonstration-feature";
import { GROUP_TRAINING_FEATURE } from "@/utils/group-training-feature";
import { TEACHER_AVAILABILITY_FEATURE } from "@/utils/teacher-availability-feature";
import { GRICS_IMPORT_PERMISSION } from "@/utils/grics-import-link";
import { PERMISSION_EXAM_SUPERVISION } from "@/utils/exam-supervision-feature";
import {
  FOLLOWED_STUDENTS_PATH, PERMISSION_SPECIALIST_FOLLOW_UP, SPECIALIST_CALENDAR_PATH, SPECIALIST_DASHBOARD_PATH,
  SPECIALIST_GROUP_ATTENDANCE_PATH, SPECIALIST_GROUP_PROGRESSION_PATH, SPECIALIST_SHEET_PATH, SPECIALIST_VIEW_FEATURE,
  SPECIALISTS_MANAGEMENT_PATH,
} from "@/utils/specialist-feature";

/** The permission carried by student accounts: the menu and the section guard ask for it, never for a role. */
export const PERMISSION_STUDENT = 'Parcours.LearningModule.Élève';

const MAX_SIDEBAR_GROUPS = 10;
export const STUDENT_STAGE_PATH = '/enseignement/mon-stage';
export const STUDENT_ASSIGNMENTS_PATH = '/enseignement/assignation-individuelle';
/** Where the student's section opens (#957) — read by the menu and by use-menu-section-guard. */
export const STUDENT_DASHBOARD_PATH = '/enseignement/mon-tableau-de-bord';
/** The anchor the runtime entries are inserted after — see afterScheduleIndex. */
export const STUDENT_SCHEDULE_PATH = '/enseignement/mon-horaire';

const studentStageMenuGroup: IMenuGroup = {
  get title() { return translate('parcours.sidebar.student.internship'); },
  icon: 'briefcase',
  to: STUDENT_STAGE_PATH,
  badge: 'beta',
};

// Keep the canonical path here. The legacy `assignations-individuelles` URL redirects to it,
// and Fondation uses this exact value to synchronize the active menu section.
const studentAssignmentsMenuGroup: IMenuGroup = {
  get title() { return translate('parcours.sidebar.student.assignments'); },
  icon: 'chalkboard-user',
  to: STUDENT_ASSIGNMENTS_PATH,
  badge: 'beta',
};

export const menus = reactive<IMenuSection[]>([
  {
    get title() { return translate('parcours.sidebar.section.student'); },
    icon: 'user-graduate',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    permissions: [PERMISSION_STUDENT],
    groups: [
      // No possessive (#957): the section is already called « Élève », so « Mon calendrier » says
      // « mon » twice. « Rapports » closes the list, whatever is inserted above it.
      { get title() { return translate('parcours.sidebar.student.dashboard'); }, icon: 'dashboard', to: STUDENT_DASHBOARD_PATH, },
      { get title() { return translate('parcours.sidebar.student.calendar'); }, icon: 'calendar', to: STUDENT_SCHEDULE_PATH, },
      { get title() { return translate('parcours.sidebar.student.exams'); }, icon: 'file-circle-check', to: '/enseignement/mes-examens', },
      { get title() { return translate('parcours.sidebar.student.justifications'); }, icon: 'file-medical', to: '/enseignement/mes-justificatifs', feature: STUDENT_SUBMISSIONS_FEATURE, },
      { get title() { return translate('parcours.sidebar.student.tracking'); }, icon: 'note-sticky', to: '/enseignement/mon-suivi', },
      { get title() { return translate('parcours.sidebar.student.reports'); }, icon: 'file-lines', to: '/enseignement/mes-rapports', feature: STUDENT_REPORTS_FEATURE, },
    ],
  },
  {
    get title() { return translate('parcours.sidebar.section.teaching'); },
    icon: 'chalkboard-user',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    // The union of what is below: the « Surveillant d'examen » (#1164) holds no Enseignant, and a
    // section missing their permission would hide the only screen they have. No `feature` on the
    // section: with the flag off the supervisor must still reach Présences to read « Fonctionnalité
    // désactivée » there rather than an empty section. The « Spécialiste » left it for a section of
    // their own (#1445, below).
    permissions: ['Parcours.LearningModule.Enseignant', PERMISSION_EXAM_SUPERVISION],
    groups: [
      { get title() { return translate('parcours.sidebar.teaching.dashboard'); }, icon: 'dashboard', to: '/enseignement/tableau-de-bord', permissions: ['Parcours.LearningModule.Enseignant'], },
      { get title() { return translate('parcours.sidebar.teaching.calendar'); }, icon: 'calendar-days', to: '/enseignement/charge-annuelle', permissions: ['Parcours.LearningModule.Enseignant'], },
      // #1164: the supervisor mode lives on this very page, locked for a supervisor-only account.
      { get title() { return translate('parcours.sidebar.teaching.attendance'); }, icon: 'hand', to: '/enseignement/presences', permissions: ['Parcours.LearningModule.Enseignant', PERMISSION_EXAM_SUPERVISION], },
      { get title() { return translate('parcours.sidebar.teaching.exams'); }, icon: 'file-circle-check', to: '/enseignement/examens', permissions: ['Parcours.LearningModule.Enseignant'] },
      {
        id: 'enseignement-assignations',
        get label() { return translate('parcours.sidebar.teaching.group'); },
        icon: 'chalkboard-user',
        iconOnly: false,
        permissions: ['Parcours.LearningModule.Enseignant'],
        // #812: the whole group sub-menu withdraws with the « Formation par groupes » flag.
        // On the GROUP, not the section: the section-index clamp of use-menu-section-guard
        // only counts sections by permissions, and must stay right.
        feature: GROUP_TRAINING_FEATURE,
        items: [
          {
            module: 'Parcours.LearningModule',
            items: [
              { get title() { return translate('parcours.sidebar.teaching.attendance'); }, icon: 'users', to: '/enseignement/assiduite' },
              { get title() { return translate('parcours.sidebar.teaching.progress'); }, icon: 'chart-line', to: '/enseignement/progression-groupe', badge: 'beta' },
            ],
          },
        ],
      },
      {
        id: 'enseignement-individualise',
        get label() { return translate('parcours.sidebar.teaching.individualized'); },
        icon: 'user',
        iconOnly: false,
        permissions: ['Parcours.LearningModule.Enseignant'],
        items: [
          {
            module: 'Parcours.LearningModule',
            items: [
              { get title() { return translate('parcours.sidebar.teaching.assignments'); }, icon: 'chalkboard-user', to: '/enseignement/assignations' },
              { get title() { return translate('parcours.sidebar.teaching.consolidatedView'); }, icon: 'table', to: '/enseignement/vue-consolidee' },
              // #821 — a plain teacher gives and follows courses too, yet cannot see the Planification
              // section this route lives in: same screen, reached from a section they do see.
              { get title() { return translate('parcours.sidebar.teaching.groupCourse'); }, icon: 'people-group', to: '/planification/cours-de-groupe' },
            ],
          },
        ],
      },
      { get title() { return translate('parcours.sidebar.teaching.reports'); }, icon: 'file-lines', to: '/enseignement/rapports', permissions: ['Parcours.LearningModule.Enseignant'] }
    ],
  },
  {
    get title() { return translate('parcours.sidebar.section.planning'); },
    icon: 'calendar',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    // The ATE permissions outlive the move of « ATE & Stages » to Gestion (#1180): a teacher who
    // supervises internships still finds the work-study time report under Planification › Rapports.
    permissions: [
      'Parcours.LearningModule.EnseignementÉlargi',
      'Parcours.LearningModule.Administrateur',
      'Parcours.LearningModule.Superviseur',
      'Parcours.ATE.Administrateur',
      'Parcours.ATE.Enseignant',
      'Parcours.ATE.Superviseur',
      'Parcours.ATE.Visiteur',
    ],
    groups: [
      { get title() { return translate('parcours.sidebar.planning.dashboard'); }, icon: 'dashboard', to: '/planification/tableau-de-bord', },
      {
        // Both answer « qui peut donner quoi, et quand » — one by competence, the other by the
        // days somebody is missing. They belong under one heading (#720).
        id: 'planification-disponibilites',
        get label() { return translate('parcours.sidebar.planning.availability'); },
        icon: 'calendar-check',
        iconOnly: false,
        items: [
          {
            permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur', 'Parcours.LearningModule.EnseignementÉlargi'],
            items: [
              {
                get title() { return translate('parcours.sidebar.planning.byCompetency'); }, icon: 'calendar-check', to: '/planification/disponibilites',
                feature: TEACHER_AVAILABILITY_FEATURE
              },
            ],
          },
          {
            // Naming every absent teacher of the centre is staff business, not a teacher's —
            // same audience as the endpoint's own guard.
            permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur'],
            items: [
              { get title() { return translate('parcours.sidebar.planning.absences'); }, icon: 'user-clock', to: '/planification/absences' },
            ],
          },
        ],
      },
      {
        id: 'planification-etudiants',
        get label() { return translate('parcours.sidebar.planning.admission'); },
        icon: 'user-graduate',
        iconOnly: false,
        items: [
          {
            permissions: ['Parcours.LearningModule.Superviseur'],
            items: [
              { get title() { return translate('parcours.sidebar.planning.students'); }, icon: 'user-group', to: '/planification/etudiants' },
              { get title() { return translate('parcours.sidebar.planning.attendanceRecords'); }, icon: 'user-graduate', to: '/planification/frequentations' },
            ],
          },
        ],
      },
      {
        id: 'planification-groupes',
        get label() { return translate('parcours.sidebar.planning.groups'); },
        icon: 'chalkboard',
        iconOnly: false,
        permissions: ['Parcours.LearningModule.Superviseur'],
        // #812 — same withdrawal as the Enseignement « Groupe » sub-menu above.
        feature: GROUP_TRAINING_FEATURE,
        items: [
          {
            permissions: ['Parcours.LearningModule.Superviseur'],
            items: [
              { get title() { return translate('parcours.sidebar.planning.groups'); }, icon: 'chalkboard', to: '/planification/vue-ensemble' },
              { get title() { return translate('parcours.sidebar.planning.solver'); }, icon: 'wand-magic-sparkles', to: '/planification/simulation', badge: 'beta' },
            ],
          },
        ],
      },
      {
        get title() { return translate('parcours.sidebar.planning.individualizedStudents'); }, icon: 'user', to: '/planification/individualise',
      },
      // « ATE & Stages » moved to Gestion (#1180), after the enterprises of Gestion › Externe (#1167).
      { get title() { return translate('parcours.sidebar.planning.reports'); }, icon: 'file-lines', to: '/planification/rapports' }
    ],
  },
  {
    get title() { return translate('parcours.sidebar.section.management'); },
    icon: 'users-gear',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    // The union of the permissions of the groups below. A permission missing here makes the
    // section invisible and therefore its screens unreachable, whatever the rights carried
    // further down — that is what deprived the administrators of sites, privacy, audit and
    // workflows of the whole section. The three lesser ATE permissions come with Gestion ›
    // Externe (#1167), whose Entreprises entry they open.
    permissions: [
      'Parcours.LearningModule.Administrateur',
      'Parcours.LearningModule.Superviseur',
      'Parcours.ATE.Administrateur',
      'Parcours.ATE.Enseignant',
      'Parcours.ATE.Superviseur',
      'Parcours.ATE.Visiteur',
      'Parcours.Locations.Administrateur',
      'Parcours.Demonstration.Administrateur',
      'Parcours.Demonstration.Concepteur',
      'Parcours.Demonstration.Démonstrateur',
      'Parcours.Demonstration.Observateur',
      'Core.Privacy.Administrateur',
      'Core.Audit.Administrateur',
      'Core.Audit.Consultation',
      'Core.Features.Manage',
      'Core.Workflow.Manage',
    ],
    groups: [
      // The director's dashboard: every card behind it is served to Administrateur or
      // Superviseur only. Without the permissions here, anyone else the section admits (an ATE
      // teacher since #1167) would see an entry whose cards all fail; section-landing-guard.ts
      // then opens the section on their first visible entry instead.
      {
        get title() { return translate('parcours.sidebar.management.dashboard'); }, icon: 'dashboard', to: '/gestion/tableau-de-bord', module: 'Parcours.LearningModule',
        permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur'],
      },
      {
        id: 'gestion-supervision',
        get label() { return translate('parcours.sidebar.management.supervision'); },
        icon: 'users-gear',
        iconOnly: false,
        // Each group carries the union of its blocks' permissions: a group all of whose items
        // are filtered out would still be displayed, folded onto "Vous n'avez accès à aucuns
        // modules."
        permissions: ['Parcours.LearningModule.Superviseur'],
        items: [
          {
            permissions: ['Parcours.LearningModule.Superviseur'],
            items: [
              { get title() { return translate('parcours.sidebar.management.teachers'); }, icon: 'chalkboard-user', to: '/gestion/enseignants' },
              // #1445 — every specialist, teaching or not, and the registration of the others.
              { get title() { return translate('parcours.sidebar.management.specialists'); }, icon: 'user-doctor', to: SPECIALISTS_MANAGEMENT_PATH, feature: SPECIALIST_VIEW_FEATURE, badge: 'beta' },
              { get title() { return translate('parcours.sidebar.management.hiring'); }, icon: 'user-plus', to: '/gestion/embauches', badge: 'beta' },
            ],
          },
        ],
      },
      // What is taught: revised all year long, hence first.
      {
        id: 'gestion-pedagogie',
        get label() { return translate('parcours.sidebar.management.pedagogy'); },
        icon: 'graduation-cap',
        iconOnly: false,
        permissions: ['Parcours.ATE.Administrateur'],
        items: [
          {
            permissions: ['Parcours.ATE.Administrateur'],
            items: [
              // What the Ministry publishes (Agora, read-only), first: the programmes below descend from it.
              // The backend routes ask for Administrateur or Superviseur of the programmes, so the entry does too.
              {
                get title() { return translate('parcours.sidebar.management.meqReferencePrograms'); }, icon: 'landmark', to: '/gestion/referentiel-meq', badge: 'beta',
                permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur'],
              },
              { get title() { return translate('parcours.sidebar.management.programsAndCompetencies'); }, icon: 'box-archive', to: '/gestion/programmes' },
              { get title() { return translate('parcours.sidebar.management.templates'); }, icon: 'layer-group', to: '/gestion/gabarits' },
              { get title() { return translate('parcours.sidebar.management.references'); }, icon: 'star-half-stroke', to: '/gestion/references' },
            ],
          },
        ],
      },
      // Where and when it is taught: set at the start of the year, rarely touched afterwards.
      {
        id: 'gestion-etablissement',
        get label() { return translate('parcours.sidebar.management.establishment'); },
        icon: 'city',
        iconOnly: false,
        permissions: [
          'Parcours.Locations.Administrateur',
          'Parcours.ATE.Administrateur',
        ],
        items: [
          {
            permissions: ['Parcours.Locations.Administrateur'],
            items: [
              { get title() { return translate('parcours.sidebar.management.centres'); }, icon: 'building', to: '/gestion/centres' },
              { get title() { return translate('parcours.sidebar.management.facilitiesAndResources'); }, icon: 'door-open', to: '/gestion/locaux-ressources' },
            ],
          },
          {
            permissions: ['Parcours.ATE.Administrateur'],
            items: [
              { get title() { return translate('parcours.sidebar.management.calendars'); }, icon: 'calendar', to: '/core/calendar' },
              { get title() { return translate('parcours.sidebar.management.schedulePatterns'); }, icon: 'clock', to: '/core/schedule-patterns' },
            ],
          },
        ],
      },
      // What lies outside the service centre (#1167): the partner enterprises and the exam
      // venues, which are the rooms of those enterprises since #1166.
      {
        id: 'gestion-externe',
        get label() { return translate('parcours.sidebar.management.external'); },
        icon: 'building',
        iconOnly: false,
        // Every ATE permission, not only Administrateur: the enterprises page opens to anyone
        // GET /api/ate/enterprises accepts, and the seeded Planificateur (ATE Visiteur +
        // Superviseur) kept that entry under Planification before the move.
        permissions: [
          'Parcours.ATE.Administrateur',
          'Parcours.ATE.Enseignant',
          'Parcours.ATE.Superviseur',
          'Parcours.ATE.Visiteur',
          'Parcours.Locations.Administrateur',
        ],
        items: [
          {
            module: 'Parcours.ATE',
            items: [
              { get title() { return translate('parcours.sidebar.management.enterprises'); }, icon: 'building', to: '/gestion/entreprises' },
            ],
          },
          {
            permissions: ['Parcours.Locations.Administrateur'],
            items: [
              { get title() { return translate('parcours.sidebar.management.examVenues'); }, icon: 'location-dot', to: '/gestion/lieux-examen' },
            ],
          },
        ],
      },
      // The internships of the work-study programs, moved from Planification (#1180) next to the
      // enterprises that host them.
      {
        id: 'gestion-ate',
        get label() { return translate('parcours.sidebar.management.ateAndInternships'); },
        icon: 'briefcase',
        iconOnly: false,
        badge: 'beta',
        // Every ATE permission, as for Externe: the section also admits users without the ATE
        // module, and a group whose only block they cannot see would still be displayed, empty.
        permissions: [
          'Parcours.ATE.Administrateur',
          'Parcours.ATE.Enseignant',
          'Parcours.ATE.Superviseur',
          'Parcours.ATE.Visiteur',
        ],
        items: [
          {
            module: 'Parcours.ATE',
            items: [
              { get title() { return translate('parcours.sidebar.management.internships'); }, icon: 'briefcase', to: '/gestion/stages' },
            ],
          },
        ],
      },
      // Governance and cross-cutting tooling: opened occasionally, by distinct profiles.
      {
        id: 'gestion-systeme',
        get label() { return translate('parcours.sidebar.management.system'); },
        icon: 'sliders',
        iconOnly: false,
        permissions: [
          'Core.Privacy.Administrateur',
          'Core.Audit.Administrateur',
          'Core.Audit.Consultation',
          'Core.Features.Manage',
          'Core.Workflow.Manage',
          'Parcours.Demonstration.Administrateur',
          'Parcours.Demonstration.Concepteur',
          'Parcours.Demonstration.Démonstrateur',
          'Parcours.Demonstration.Observateur',
        ],
        items: [
          {
            permissions: ['Core.Privacy.Administrateur'],
            items: [
              { get title() { return translate('parcours.sidebar.management.privacy'); }, icon: 'shield-halved', to: '/gestion/confidentialite/parametres' },
            ],
          },
          {
            permissions: ['Core.Audit.Administrateur', 'Core.Audit.Consultation'],
            items: [
              { get title() { return translate('parcours.sidebar.management.auditLog'); }, icon: 'clipboard-list', to: '/gestion/audit' },
            ],
          },
          {
            // The Fondation's own feature-flag screen. Parcours declares flags but had no way to
            // reach the page that toggles them. Every endpoint behind that screen requires
            // `Manage`, so the module alone would open a page that can only fail.
            module: 'Core.Features',
            permissions: ['Core.Features.Manage'],
            items: [
              { get title() { return translate('parcours.sidebar.management.features'); }, icon: 'toggle-on', to: '/core/features' },
            ],
          },
          {
            permissions: ['Core.Workflow.Manage'],
            items: [
              { get title() { return translate('parcours.sidebar.management.workflows'); }, icon: 'diagram-project', to: '/workflow', badge: 'beta' },
            ],
          },
          {
            // The four permissions open the page; it is the view that then decides
            // which gestures are offered there.
            permissions: [
              'Parcours.Demonstration.Administrateur',
              'Parcours.Demonstration.Concepteur',
              'Parcours.Demonstration.Démonstrateur',
              'Parcours.Demonstration.Observateur',
            ],
            items: [
              { get title() { return translate('parcours.sidebar.management.demonstration'); }, icon: 'flask', to: '/gestion/demonstration', feature: DEMONSTRATION_FEATURE, },
            ],
          },
        ],
      },
      {
        // The management reports are all served by Parcours.LearningModule under
        // Administrateur or Superviseur; without one of the two, the page has nothing to offer.
        get title() { return translate('parcours.sidebar.management.reports'); }, icon: 'file-lines', to: '/gestion/rapports',
        module: 'Parcours.LearningModule',
        permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur'],
      }
    ],
  },
  // #1445 — the specialist's own section: the periods booked with them, their calendar, the groups
  // of their students in consultation and their students, without the Enseignant permission. The
  // permission AND the flag sit on the section: the view is off by default, and every administrator
  // holds « Suivi spécialisé » too, so a section gated below only would put an empty button in their
  // switcher. use-menu-section-guard counts the sections with their flag. Last, so adding it shifts
  // no section anyone had open. Its entries follow Enseignement's order.
  {
    get title() { return translate('parcours.sidebar.section.specialist'); },
    icon: 'user-doctor',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    permissions: [PERMISSION_SPECIALIST_FOLLOW_UP],
    feature: SPECIALIST_VIEW_FEATURE,
    // The section is in beta (#1445): its dashboard and « Élèves suivis » say so in the menu, as
    // « Progression » does. Not the switcher's button: the chip did not fit there.
    groups: [
      { get title() { return translate('parcours.sidebar.teaching.dashboard'); }, icon: 'dashboard', to: SPECIALIST_DASHBOARD_PATH, badge: 'beta' },
      { get title() { return translate('parcours.sidebar.teaching.calendar'); }, icon: 'calendar-days', to: SPECIALIST_CALENDAR_PATH },
      { get title() { return translate('parcours.sidebar.teaching.attendance'); }, icon: 'hand', to: SPECIALIST_SHEET_PATH },
      {
        id: 'suivi-groupe',
        get label() { return translate('parcours.sidebar.teaching.group'); },
        icon: 'chalkboard-user',
        iconOnly: false,
        // Read only, and withdrawn with the « Formation par groupes » flag like Enseignement's.
        feature: GROUP_TRAINING_FEATURE,
        items: [
          {
            items: [
              { get title() { return translate('parcours.sidebar.teaching.attendance'); }, icon: 'users', to: SPECIALIST_GROUP_ATTENDANCE_PATH, get badge() { return translate('parcours.sidebar.specialist.consultation'); }, badgeColor: 'grey' },
              { get title() { return translate('parcours.sidebar.teaching.progress'); }, icon: 'chart-line', to: SPECIALIST_GROUP_PROGRESSION_PATH, get badge() { return translate('parcours.sidebar.specialist.consultation'); }, badgeColor: 'grey' },
            ],
          },
        ],
      },
      { get title() { return translate('parcours.sidebar.teaching.followedStudents'); }, icon: 'clipboard-user', to: FOLLOWED_STUDENTS_PATH, badge: 'beta' },
    ],
  },
]);

/** Keep both ancestor permission unions in step, so an unavailable import leaves no empty group. */
export function setGricsImportMenuVisible(visible: boolean): void {
  const section = menus.find(item => item.groups.some(group => group.id === 'gestion-systeme'));
  const group = section?.groups.find(item => item.id === 'gestion-systeme');
  if (!section || !group?.items) return;

  const existing = group.items.findIndex(item => item.permissions?.includes(GRICS_IMPORT_PERMISSION));
  if (visible && existing === -1) {
    const demo = group.items.findIndex(block => block.items.some(item => item.feature === DEMONSTRATION_FEATURE));
    group.items.splice(demo === -1 ? group.items.length : demo, 0, {
      permissions: [GRICS_IMPORT_PERMISSION],
      items: [{ get title() { return translate('parcours.sidebar.management.gricsImports'); }, icon: 'cloud-arrow-down', to: '/gestion/importations-grics' }],
    });
  } else if (!visible && existing !== -1) {
    group.items.splice(existing, 1);
  }
  for (const entry of [section, group]) {
    entry.permissions = (entry.permissions ?? []).filter(permission => permission !== GRICS_IMPORT_PERMISSION);
    if (visible) entry.permissions.push(GRICS_IMPORT_PERMISSION);
  }
}

/**
 * The runtime entries land after « Calendrier », looked up by its path rather than by a fixed
 * index: the dashboard took position 0 in #957, and an index would have slipped « Stage »
 * between the dashboard and the schedule. Falling back to the top only if the schedule ever
 * leaves the section.
 */
function afterScheduleIndex(groups: IMenuGroup[]): number {
  const scheduleIndex = groups.findIndex(group => group.to === STUDENT_SCHEDULE_PATH);
  return scheduleIndex === -1 ? 1 : scheduleIndex + 1;
}

export function setStudentStageMenuVisible(visible: boolean): void {
  const studentSection = menus.find(section => section.permissions?.includes(PERMISSION_STUDENT));
  if (!studentSection) return;

  const existingIndex = studentSection.groups.findIndex(group => group.to === STUDENT_STAGE_PATH);
  if (visible && existingIndex === -1) {
    studentSection.groups.splice(afterScheduleIndex(studentSection.groups), 0, studentStageMenuGroup);
  } else if (!visible && existingIndex !== -1) {
    studentSection.groups.splice(existingIndex, 1);
  }
}

export function setStudentAssignmentsMenuVisible(visible: boolean): void {
  const studentSection = menus.find(section => section.permissions?.includes(PERMISSION_STUDENT));
  if (!studentSection) return;

  const existingIndex = studentSection.groups.findIndex(group => group.to === STUDENT_ASSIGNMENTS_PATH);
  if (visible && existingIndex === -1) {
    const stageIndex = studentSection.groups.findIndex(group => group.to === STUDENT_STAGE_PATH);
    studentSection.groups.splice(
      stageIndex === -1 ? afterScheduleIndex(studentSection.groups) : stageIndex + 1,
      0,
      studentAssignmentsMenuGroup);
  } else if (!visible && existingIndex !== -1) {
    studentSection.groups.splice(existingIndex, 1);
  }
}
